import {
  isWorkbookErrorCell,
  type WorkbookCell,
  type WorkbookErrorCell,
  type WorkbookFilter,
} from "@erp/shared-types";
import type { ColumnType } from "../formula";
import { compare, parseDecimal } from "./decimal";

/**
 * A pivot reads cells as the engine hands a Worksheet's rows on: a Number as
 * exact decimal text, a Date as ISO `YYYY-MM-DD` text, TRUE/FALSE as
 * `true`/`false`, Empty as null (or, for Text, also `''`), and Error cells.
 */

/** A configuration value (a filter's) that isn't its Field's type: the Run fails. */
export class PivotRunError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PivotRunError";
  }
}

/** Empty, as Is Empty matches it: null, or empty text in a Text Field. */
export function isEmptyCell(cell: WorkbookCell, type: ColumnType): boolean {
  return cell === null || (type === "text" && cell === "");
}

const DATE = /^(\d{4})-(\d{2})-(\d{2})/;
const TYPE_NAMES: Readonly<Record<ColumnType, string>> = {
  text: "Text",
  decimal: "Number",
  date: "Date",
  datetime: "Date and time",
  boolean: "TRUE/FALSE",
};
/** An ISO 8601 date and time, as a Date and time cell holds it (UTC). */
const DATETIME =
  /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,6})?)?Z?$/;

/** The ISO date a cell holds (a Date, or Text starting with one), else null. */
export function readDate(
  cell: string,
): { year: number; month: number; day: number } | null {
  const match = DATE.exec(cell.trim());
  if (!match) return null;
  const [year, month, day] = match.slice(1).map(Number) as [
    number,
    number,
    number,
  ];
  return month >= 1 && month <= 12 && day >= 1 && day <= 31
    ? { year, month, day }
    : null;
}

/** Boolean text as the evaluator and the SQL engine read it (true/t/1, false/f/0), else null. */
export function readBoolean(text: string): boolean | null {
  const lower = text.trim().toLowerCase();
  if (["true", "t", "1"].includes(lower)) return true;
  if (["false", "f", "0"].includes(lower)) return false;
  return null;
}

/** Text in the engine's binary collation: code point order. */
export function compareText(left: string, right: string): number {
  const a = [...left];
  const b = [...right];
  for (let index = 0; index < Math.min(a.length, b.length); index++) {
    const difference =
      (a[index]?.codePointAt(0) ?? 0) - (b[index]?.codePointAt(0) ?? 0);
    if (difference) return difference;
  }
  return a.length - b.length;
}

/** Compares two present values of one Declared type, as the engine orders them. */
export function compareValues(
  left: string,
  right: string,
  type: ColumnType,
): number {
  if (type === "decimal") {
    const a = parseDecimal(left);
    const b = parseDecimal(right);
    if (a && b) return compare(a, b);
  }
  if (type === "boolean") {
    const a = readBoolean(left);
    const b = readBoolean(right);
    if (a !== null && b !== null) return Number(a) - Number(b);
  }
  return compareText(left, right);
}

/** A filter value cast to its Field's type; null for empty text, which matches nothing. */
function filterValue(
  value: string,
  filter: WorkbookFilter,
  type: ColumnType,
): string | null {
  if (type === "text") return value;
  if (value === "") return null;
  const reads: Record<
    Exclude<ColumnType, "text">,
    (text: string) => boolean
  > = {
    decimal: (text) => parseDecimal(text) !== null,
    date: (text) => readDate(text) !== null && text.trim().length === 10,
    datetime: (text) => DATETIME.test(text.trim()),
    boolean: (text) => readBoolean(text) !== null,
  };
  const valid = reads[type](value);
  if (!valid)
    throw new PivotRunError(
      `The filter on '${filter.column}' compares with '${value}', which isn't a ${TYPE_NAMES[type]} (#VALUE!).`,
    );
  return value.trim();
}

function isOneOf(
  cell: string | number | boolean | null,
  filter: WorkbookFilter,
  type: ColumnType,
): boolean {
  const values = filter.values ?? [];
  if (isEmptyCell(cell, type)) return values.includes("");
  const text = String(cell);
  return values.some((value) => {
    if (value === "") return false;
    const cast = filterValue(value, filter, type);
    return cast !== null && compareValues(text, cast, type) === 0;
  });
}

const COMPARISONS: Readonly<Record<string, (order: number) => boolean>> = {
  eq: (order) => order === 0,
  neq: (order) => order !== 0,
  gt: (order) => order > 0,
  gte: (order) => order >= 0,
  lt: (order) => order < 0,
  lte: (order) => order <= 0,
};

function matchesValue(
  cell: string | number | boolean | null,
  filter: WorkbookFilter,
  type: ColumnType,
): boolean {
  switch (filter.operator) {
    case "is-null":
      return isEmptyCell(cell, type);
    case "is-not-null":
      return !isEmptyCell(cell, type);
    case "is-one-of":
      return isOneOf(cell, filter, type);
    case "is-not-one-of":
      return !isOneOf(cell, filter, type);
    default:
      break;
  }
  // As in the engine's SQL: a comparison with a missing value matches nothing.
  if (cell === null) return false;
  const text = String(cell);
  if (filter.operator === "contains") return text.includes(filter.value);
  const value = filterValue(filter.value, filter, type);
  const test = COMPARISONS[filter.operator];
  return value !== null && test !== undefined
    ? test(compareValues(text, value, type))
    : false;
}

/**
 * Whether a cell matches a filter, as a columnar Worksheet's filters match:
 * only the error filters match an Error cell.
 */
export function matchesFilter(
  cell: WorkbookCell,
  filter: WorkbookFilter,
  type: ColumnType,
): boolean {
  if (filter.operator === "is-error") return isWorkbookErrorCell(cell);
  if (filter.operator === "is-not-error") return !isWorkbookErrorCell(cell);
  if (filter.operator === "is-na")
    return isWorkbookErrorCell(cell) && cell.error === "#N/A";
  if (isWorkbookErrorCell(cell)) return false;
  return matchesValue(cell, filter, type);
}

export function valueError(
  cell: string,
  field: string,
  type: ColumnType,
): WorkbookErrorCell {
  return {
    error: "#VALUE!",
    cause: `'${cell}' in '${field}' isn't a ${TYPE_NAMES[type]}`,
  };
}
